import { useEffect, useRef, useCallback } from 'react';

const MAX_LOGS = 100;

type LogEntry = {
  level: 'log' | 'warn' | 'error' | 'info' | 'debug';
  message: string;
  timestamp: number;
};

let sharedLogs: LogEntry[] = [];
let installed = false;

const SENSITIVE_PATTERNS = [
  { pattern: /token["']?\s*[:=]\s*["']?[a-zA-Z0-9_\-]{20,}["']?/gi, replacement: 'token=[REDACTED]' },
  { pattern: /bearer\s+[a-zA-Z0-9_\-.]+/gi, replacement: 'Bearer [REDACTED]' },
  { pattern: /authorization["']?\s*[:=]\s*["']?[^"'\s]+["']?/gi, replacement: 'authorization=[REDACTED]' },
  { pattern: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, replacement: '[EMAIL]' },
  { pattern: /password["']?\s*[:=]\s*["']?[^"'\s]+["']?/gi, replacement: 'password=[REDACTED]' },
];

function maskSensitiveData(text: string): string {
  return SENSITIVE_PATTERNS.reduce((acc, { pattern, replacement }) => {
    return acc.replace(pattern, replacement);
  }, text);
}

function stringify(args: unknown[]): string {
  const raw = args
    .map((arg) => {
      if (typeof arg === 'string') return arg;
      if (arg instanceof Error) return `${arg.name}: ${arg.message}\n${arg.stack ?? ''}`;
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    })
    .join(' ');
  return maskSensitiveData(raw);
}

function installCapture() {
  if (installed) return;
  installed = true;

  const originals = {
    log: console.log,
    warn: console.warn,
    error: console.error,
    info: console.info,
    debug: console.debug,
  };

  const push = (level: LogEntry['level'], args: unknown[]) => {
    const message = stringify(args);
    sharedLogs.push({ level, message, timestamp: Date.now() });
    if (sharedLogs.length > MAX_LOGS) {
      sharedLogs = sharedLogs.slice(sharedLogs.length - MAX_LOGS);
    }
  };

  console.log = (...args: unknown[]) => {
    push('log', args);
    originals.log.apply(console, args);
  };
  console.warn = (...args: unknown[]) => {
    push('warn', args);
    originals.warn.apply(console, args);
  };
  console.error = (...args: unknown[]) => {
    push('error', args);
    originals.error.apply(console, args);
  };
  console.info = (...args: unknown[]) => {
    push('info', args);
    originals.info.apply(console, args);
  };
  console.debug = (...args: unknown[]) => {
    push('debug', args);
    originals.debug.apply(console, args);
  };
}

export function useConsoleCapture(): () => string[] {
  const captureRef = useRef(() => sharedLogs.map((l) => `[${l.level}] ${new Date(l.timestamp).toISOString()} ${l.message}`));

  useEffect(() => {
    installCapture();
  }, []);

  return useCallback(() => captureRef.current(), []);
}
